import { useEffect, ReactNode } from 'react';
import { Routes, Route, Navigate } from 'react-router-dom';
import * as Sentry from '@sentry/react';
import Hello from './routes/Hello';
import Login from './routes/Login';
import AppHome from './routes/AppHome';
import Admin from './routes/Admin';
import SentryTest from './routes/SentryTest';
import Tasks from './routes/Tasks';
import Calendar from './routes/Calendar';
import CalendarSettings from './routes/CalendarSettings';
import PublicBooking from './routes/PublicBooking';
import Billing from './routes/Billing';
import AccountBilling from './routes/account/Billing';
import AdminBilling from './routes/AdminBilling';
import RecruitingPipeline from './routes/RecruitingPipeline';
import RecruitingRecruits from './routes/RecruitingRecruits';
import RecruitingApplicationNew from './routes/RecruitingApplicationNew';
import RecruitingDossier from './routes/RecruitingDossier';
import RecruitingLinks from './routes/RecruitingLinks';
import PublicApply from './routes/PublicApply';
import ComingSoon from './routes/ComingSoon';
import RequireAuth from './components/RequireAuth';
import AppShell from './components/AppShell';
import { useAuth } from './lib/auth';

const SentryRoutes = Sentry.withSentryReactRouterV6Routing(Routes);

function Protected({
  children,
  role,
}: {
  children: ReactNode;
  role?: 'global_superadmin';
}) {
  return (
    <RequireAuth role={role}>
      <AppShell>{children}</AppShell>
    </RequireAuth>
  );
}

export default function App() {
  const hydrate = useAuth((s) => s.hydrate);
  useEffect(() => {
    hydrate();
  }, [hydrate]);

  return (
    <SentryRoutes>
      <Route path="/" element={<Hello />} />
      <Route path="/login" element={<Login />} />
      <Route path="/book/:slug" element={<PublicBooking />} />
      <Route path="/apply/r/:token" element={<PublicApply />} />
      <Route path="/app" element={<Protected><AppHome /></Protected>} />
      <Route path="/tasks" element={<Protected><Tasks /></Protected>} />
      <Route path="/calendar" element={<Protected><Calendar /></Protected>} />
      <Route
        path="/calendar/settings"
        element={<Protected><CalendarSettings /></Protected>}
      />
      <Route path="/billing" element={<Protected><Billing /></Protected>} />
      <Route
        path="/account/billing"
        element={<Protected><AccountBilling /></Protected>}
      />
      <Route
        path="/admin"
        element={<Protected role="global_superadmin"><Admin /></Protected>}
      />
      <Route
        path="/admin/billing"
        element={<Protected role="global_superadmin"><AdminBilling /></Protected>}
      />
      <Route
        path="/recruiting/pipeline"
        element={<Protected><RecruitingPipeline /></Protected>}
      />
      <Route
        path="/recruiting/recruits"
        element={<Protected><RecruitingRecruits /></Protected>}
      />
      <Route
        path="/recruiting/applications/new"
        element={<Protected><RecruitingApplicationNew /></Protected>}
      />
      <Route
        path="/recruiting/links"
        element={<Protected><RecruitingLinks /></Protected>}
      />
      <Route
        path="/recruiting/recruits/:id"
        element={<Protected><RecruitingDossier /></Protected>}
      />
      <Route
        path="/recruiting/coming-soon"
        element={<Protected><ComingSoon /></Protected>}
      />
      <Route path="/__sentry_test" element={<SentryTest />} />
      <Route path="*" element={<Navigate to="/" replace />} />
    </SentryRoutes>
  );
}
